/**
 * Compara o nome impresso na etiqueta com o nome cadastrado do morador.
 *
 * Comparacao literal nao serve, e por dois motivos que sao a regra e nao a
 * excecao numa etiqueta:
 *
 * 1. O nome vem CURTO. O cadastro tem "Gabriel Pereira Firmo de Aguiar" e a
 *    loja imprime "Gabriel Aguiar" — ou "Gabriel Firmo", ou "Gabriel Firmo de
 *    Aguiar". Medir a cobertura pelo tamanho do CADASTRO reprova justamente o
 *    caso comum: dois de quatro pedacos e 50%, abaixo de qualquer corte
 *    razoavel, mesmo o palpite estando certo. Por isso o que conta aqui e
 *    quantos pedacos bateram e QUAIS — nome + sobrenome ja separa os xarás de
 *    um predio.
 *
 * 2. O nome vem ERRADO. "AGUIAAR", "GABIEL", "AGUAIR": o OCR dobra letra, come
 *    letra e troca vizinhas de lugar. Por isso a comparacao aceita uma
 *    distancia de edicao proporcional ao tamanho da palavra — e NENHUMA em
 *    palavra curta, onde uma letra ja e outro nome ("ANA"/"ANO", "LUIZ"/"LUIS"
 *    sao pessoas diferentes no mesmo predio).
 *
 * O que este arquivo nao faz e decidir. Ele devolve a forca do palpite; quem
 * chama (`matchUnit`) e que escolhe entre preencher sozinho e perguntar "esse
 * e o destinatario?" — porque um "Gabriel" solto e um palpite util, mas raso
 * demais para virar campo preenchido sem o porteiro olhar.
 */

import { normalizeText } from './parseLabel.js';

/** Conectivos e sufixos: nao identificam ninguem, mas somariam como acerto. */
const STOPWORDS = new Set([
  'DE',
  'DA',
  'DO',
  'DAS',
  'DOS',
  'E',
  'DI',
  'DEL',
  'VON',
  'VAN',
  'SR',
  'SRA',
  'DR',
  'DRA',
]);

/**
 * Quebra um nome nos pedacos que valem comparacao.
 *
 * Pedaco com menos de 3 letras sai: sao as iniciais ("Gabriel P. F. de
 * Aguiar") e as sobras do OCR. Elas nunca provam nada sozinhas e, se contadas
 * como acerto, um "P" solto na etiqueta viraria meio sobrenome.
 */
export function nameTokens(value: string): string[] {
  return normalizeText(value)
    .replace(/[^A-Z]+/g, ' ')
    .trim()
    .split(' ')
    .filter((token) => token.length >= 3 && !STOPWORDS.has(token));
}

/**
 * Quantos erros de leitura tolerar numa palavra desse tamanho.
 *
 * Zero ate 4 letras e a decisao mais importante da funcao: com tolerancia 1,
 * "ANA" casaria com "ANO", "IVO" e "ANE", e o sistema entregaria a encomenda
 * do vizinho. A partir de 5 letras uma diferenca ja e quase sempre o OCR, nao
 * outra pessoa.
 *
 * Duas diferencas so a partir de 9 letras, e o corte esta ai por um par
 * concreto: PEREIRA e FERREIRA estao a duas edicoes um do outro e sao dois
 * sobrenomes dos mais comuns do pais. Com o teto em 8, "CAVALCANTI" continua
 * atravessando o OCR e esses dois deixam de ser a mesma pessoa.
 */
function toleranceFor(length: number): number {
  if (length <= 4) return 0;
  if (length <= 8) return 1;
  return 2;
}

/**
 * Distancia de Damerau-Levenshtein com corte em `max`.
 *
 * A transposicao (o "D" de Damerau) esta aqui porque o OCR troca letras
 * vizinhas de lugar com frequencia — "AGUAIR" por "AGUIAR" e um erro de UMA
 * edicao para ele, e de duas para o Levenshtein puro, que reprovaria a palavra
 * inteira.
 *
 * Devolve `max + 1` assim que fica claro que a distancia passou do limite: sem
 * esse corte, comparar 200 moradores contra a etiqueta inteira seria trabalho
 * a toa em quase toda comparacao, que e justamente a que nao casa.
 */
export function editDistance(a: string, b: string, max: number): number {
  if (a === b) return 0;
  if (Math.abs(a.length - b.length) > max) return max + 1;

  // Tres linhas da matriz: a anterior, a de antes dela (necessaria so para a
  // transposicao) e a que esta sendo preenchida.
  let beforePrevious: number[] = [];
  let previous = Array.from({ length: b.length + 1 }, (_, index) => index);
  let current: number[] = [];

  for (let i = 1; i <= a.length; i += 1) {
    current = [i];
    let bestInRow = i;

    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      let value = Math.min(
        previous[j]! + 1, // remocao
        current[j - 1]! + 1, // insercao
        previous[j - 1]! + cost, // substituicao
      );

      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        value = Math.min(value, beforePrevious[j - 2]! + 1); // transposicao
      }

      current[j] = value;
      if (value < bestInRow) bestInRow = value;
    }

    // A distancia nunca diminui nas linhas seguintes: se a melhor celula desta
    // linha ja passou do teto, nao ha volta.
    if (bestInRow > max) return max + 1;

    beforePrevious = previous;
    previous = current;
  }

  return previous[b.length]!;
}

export type TokenMatch = 'exact' | 'fuzzy' | null;

/** Duas palavras sao o mesmo pedaco de nome? E foi preciso perdoar erro? */
export function matchToken(a: string, b: string): TokenMatch {
  if (a === b) return 'exact';

  const tolerance = toleranceFor(Math.max(a.length, b.length));
  if (tolerance === 0) return null;
  if (Math.abs(a.length - b.length) > tolerance) return null;

  return editDistance(a, b, tolerance) <= tolerance ? 'fuzzy' : null;
}

export interface NameScore {
  /** Pedacos do nome cadastrado que aparecem na etiqueta. */
  matched: number;
  /** Quantos deles so bateram perdoando erro de leitura. */
  fuzzy: number;
  /** O primeiro nome bateu. */
  firstName: boolean;
  /** Bateu algo alem do primeiro nome — e o sobrenome que separa os xarás. */
  surname: boolean;
  /**
   * Pedacos lidos na etiqueta que o cadastro nao explica. So faz sentido
   * quando os tokens vieram da linha do destinatario: contra a etiqueta
   * inteira, "sobra" o endereco todo e o numero nao diz nada.
   */
  unexplained: number;
}

const NO_MATCH: NameScore = {
  matched: 0,
  fuzzy: 0,
  firstName: false,
  surname: false,
  unexplained: 0,
};

/**
 * Mede o nome cadastrado contra os pedacos lidos da etiqueta.
 *
 * Cada pedaco lido so pode explicar UM pedaco do cadastro (`used`): sem isso,
 * um "SILVA" impresso uma vez casaria com os dois "SILVA" de "Ana Silva
 * Silveira" e inflaria a pontuacao de um nome que so apareceu pela metade.
 */
export function scoreName(residentName: string, labelTokens: readonly string[]): NameScore {
  const tokens = nameTokens(residentName);
  if (tokens.length === 0 || labelTokens.length === 0) return NO_MATCH;

  const used = new Set<number>();
  let matched = 0;
  let fuzzy = 0;
  let firstName = false;
  let surname = false;

  tokens.forEach((token, position) => {
    // Varre a etiqueta inteira antes de aceitar um casamento aproximado: um
    // "AGUIAR" exato mais adiante vale mais do que o "AGUIAS" logo no comeco.
    let kind: TokenMatch = null;
    let at = -1;

    for (let index = 0; index < labelTokens.length; index += 1) {
      if (used.has(index)) continue;

      const attempt = matchToken(token, labelTokens[index]!);
      if (attempt === 'exact') {
        kind = 'exact';
        at = index;
        break;
      }
      if (attempt === 'fuzzy' && kind === null) {
        kind = 'fuzzy';
        at = index;
      }
    }

    if (kind === null) return;

    used.add(at);
    matched += 1;
    if (kind === 'fuzzy') fuzzy += 1;
    if (position === 0) firstName = true;
    else surname = true;
  });

  return {
    matched,
    fuzzy,
    firstName,
    surname,
    unexplained: labelTokens.length - used.size,
  };
}

/**
 * Forca do palpite.
 *
 * - `strong`: da para preencher o campo e o porteiro so conferir.
 * - `weak`: vale mostrar, mas como pergunta.
 * - `null`: nao e ninguem.
 *
 * `scoped` diz se `labelTokens` veio da linha do destinatario (e nao da
 * etiqueta inteira). So nesse caso "sobrou nome que o cadastro nao explica"
 * significa alguma coisa — e significa bastante: a etiqueta trouxe um
 * sobrenome que este morador nao tem, o que costuma ser o parente, nao ele.
 */
export type NameConfidence = 'strong' | 'weak' | null;

export function confidenceOf(score: NameScore, scoped: boolean): NameConfidence {
  if (score.matched === 0) return null;

  // Um pedaco so — o primeiro nome, ou um sobrenome solto. E o caso do
  // "Gabriel" da etiqueta contra os quatro Gabriels do predio: bom palpite,
  // mas raso demais para preencher sem perguntar.
  if (score.matched < 2 || !score.surname) return 'weak';

  // Um erro de leitura entre dois pedacos ainda e leitura; dois erros ja e
  // chute — a chance de duas palavras aproximadas apontarem para a pessoa
  // errada e alta demais para preencher sozinho.
  if (score.fuzzy >= 2) return 'weak';

  // "GABRIEL AGUIAR SILVA" lido, cadastro "Gabriel Aguiar": o SILVA que sobrou
  // pode ser outra pessoa da mesma familia. Com tres pedacos batendo o peso do
  // acerto ja supera o de um token de ruido do OCR.
  if (scoped && score.unexplained > 0 && score.matched < 3) return 'weak';

  return 'strong';
}
